import { DateTime } from 'luxon'

// ✅ Get all IANA timezones with user-friendly labels (Frontend)
export const getAllTimezones = () => {
  const timezones = [
    // US & Canada
    'America/Anchorage',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/New_York',
    'America/Toronto',
    'America/Vancouver',
    
    // Latin America
    'America/Argentina/Buenos_Aires',
    'America/Bogota',
    'America/Mexico_City',
    'America/Sao_Paulo',
    
    // Europe
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Amsterdam',
    'Europe/Brussels',
    'Europe/Rome',
    'Europe/Madrid',
    'Europe/Vienna',
    'Europe/Prague',
    'Europe/Warsaw',
    'Europe/Moscow',
    'Europe/Athens',
    'Europe/Helsinki',
    'Europe/Istanbul',
    
    // Africa
    'Africa/Cairo',
    'Africa/Johannesburg',
    'Africa/Lagos',
    'Africa/Nairobi',
    'Africa/Casablanca',
    
    // Middle East
    'Asia/Dubai',
    'Asia/Baghdad',
    'Asia/Tehran',
    'Asia/Jerusalem',
    
    // Asia
    'Asia/Bangkok',
    'Asia/Kolkata',
    'Asia/Jakarta',
    'Asia/Manila',
    'Asia/Shanghai',
    'Asia/Hong_Kong',
    'Asia/Tokyo',
    'Asia/Seoul',
    'Asia/Singapore',
    'Asia/Karachi',
    
    // Oceania
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Perth',
    'Australia/Darwin',
    'Pacific/Auckland',
    'Pacific/Fiji',
    'Pacific/Honolulu',
  ]
  
  return timezones.map(tz => {
    try {
      return {
        value: tz,
        label: formatTimezoneLabel(tz),
      }
    } catch (error) {
      console.error(`[getAllTimezones] Failed to format timezone ${tz}:`, error)
      return {
        value: tz,
        label: tz.replace(/_/g, ' '),
      }
    }
  })
}

// ✅ Format timezone label with offset and friendly name
export const formatTimezoneLabel = (timezone) => {
  try {
    if (!timezone || typeof timezone !== 'string') {
      return 'Invalid timezone'
    }
    
    const date = new Date()
    
    // Validate timezone by creating a formatter with it
    try {
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
      })
    } catch (e) {
      // Invalid timezone
      const friendlyName = timezone.replace(/_/g, ' ')
      return friendlyName
    }
    
    // Calculate offset using DateTime from Luxon for reliability
    const dtUtc = DateTime.utc()
    const dtTz = DateTime.utc().setZone(timezone)
    
    if (!dtTz.isValid) {
      const friendlyName = timezone.replace(/_/g, ' ')
      return friendlyName
    }
    
    const offsetMinutes = dtTz.offset - dtUtc.offset
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60)
    const offsetMins = Math.abs(offsetMinutes) % 60
    const offsetSign = offsetMinutes >= 0 ? '+' : '-'
    const offsetStr = `GMT${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`
    
    const friendlyName = timezone.replace(/_/g, ' ')
    return `${offsetStr} ${friendlyName}`
  } catch (error) {
    console.error(`[Timezone] Error formatting timezone: ${timezone}`, error)
    // Fallback to just the friendly name
    return timezone.replace(/_/g, ' ')
  }
}

// ✅ Get user's local timezone
export const getUserLocalTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    console.error('[Timezone:getUserLocalTimezone] Failed:', error)
    return 'America/Chicago'
  }
}

// ✅ Format date/time string for display
export const formatDateTimeForDisplay = (date, time) => {
  if (!date || !time) return ''
  const [year, month, day] = date.split('-')
  const [hours, minutes] = time.split(':')
  return `${month}/${day}/${year} ${hours}:${minutes}`
}

// ✅ Filter timezones based on search query
export const filterTimezones = (query) => {
  if (!query) return getAllTimezones()
  
  const lowerQuery = query.toLowerCase()
  return getAllTimezones().filter(tz => 
    tz.value.toLowerCase().includes(lowerQuery) ||
    tz.label.toLowerCase().includes(lowerQuery)
  )
}

// ✅ Get default timezone
export const getDefaultTimezone = () => {
  return 'America/Chicago'
}

// ✅ Get timezone abbreviation
export const getTimezoneAbbreviation = (timezone) => {
  const parts = timezone.split('/')
  return parts[parts.length - 1]
}

// ✅ Convert UTC date/time to specific timezone (Frontend version)
export const fromUTC = (utcDate, utcTime, timezone) => {
  try {
    if (!utcDate || !utcTime || !timezone) {
      throw new Error('UTC date, UTC time, and timezone are required')
    }

    // Combine UTC date and time
    const utcDateTimeString = `${utcDate}T${utcTime}:00Z`

    // Parse as UTC and convert to specified timezone
    const utcDateTime = DateTime.fromISO(utcDateTimeString)

    if (!utcDateTime.isValid) {
      throw new Error(`Invalid UTC date/time: ${utcDateTimeString}`)
    }

    const dateTimeInZone = utcDateTime.setZone(timezone)

    console.log(`[Timezone:fromUTC] Converted UTC ${utcDateTimeString} to ${timezone}: ${dateTimeInZone.toISO()}`)

    return {
      date: dateTimeInZone.toFormat('yyyy-MM-dd'),
      time: dateTimeInZone.toFormat('HH:mm'),
      iso: dateTimeInZone.toISO(),
      timezone: timezone,
    }
  } catch (error) {
    console.error('[Timezone:fromUTC] Conversion failed:', error.message)
    throw new Error(`Failed to convert from UTC: ${error.message}`)
  }
}
